import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import { letter } from "../../lib/format"
import { useMe } from "../../hooks/useMe"
import { useStartSimulado } from "../../hooks/useStartSimulado"
import { ResultHero } from "./ResultHero"
import {
  CTA_TRATAMENTO,
  CTA_TRATAMENTO_CONTINUAR,
  GARANTIA,
  LISTAS,
  PROMESSA,
  TRATAMENTO_CONTRASTE,
  TRATAMENTO_CONTRASTE_ACIMA,
  distanciaDoCorte,
  listarMaterias,
  materiasDePartida,
  materiasFechadas,
  nomearLacunas,
  passariaDoCorte,
  precoLinha,
  provaSocialLabel,
} from "../../lib/narrativa"
import { Spinner } from "../ui/Spinner"
import QuestionContent from "../QuestionContent"
import RichContent from "../RichContent"
import ReferenceText from "../ReferenceText"
import { BrandLogo } from "../ui/BrandLogo"
import type { Corte, Cortes, Oferta, ProvaSocial, ResultResponse, ReviewQuestion, SubjectResult } from "../../types"

type Filter = "all" | "wrong"

function accuracyOf(r: SubjectResult): number {
  return r.total > 0 ? Math.round((r.correct / r.total) * 100) : 0
}

/**
 * Lista de matérias com barra — usada duas vezes: "Suas matérias de partida"
 * (da mais fraca pra menos fraca, é o que a pessoa veio descobrir) e "Onde
 * você já está bem". Sem vermelho e sem "0%" gritante; o detalhe fica na
 * revisão abaixo.
 */
function SubjectBreakdown({ results, title, empty }: { results: SubjectResult[]; title: string; empty: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <h3 className="mb-3 text-sm font-bold text-navy-900">{title}</h3>
      {results.length === 0 && <p className="text-sm text-navy-500">{empty}</p>}
      <ul className="divide-y divide-slate-100">
        {results.map((r) => {
          const acc = accuracyOf(r)
          const strong = acc >= 70
          return (
            <li key={r.subject} className="py-3.5 first:pt-0 last:pb-0">
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium text-navy-800">{r.subject}</span>
                  {strong && (
                    <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-600">
                      forte
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-sm tabular-nums text-navy-400">
                  <span className="font-semibold text-navy-900">{r.correct}</span> de {r.total}
                </span>
              </div>
              <div
                className="relative mt-2 h-2.5"
                role="progressbar"
                aria-valuenow={acc}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${r.subject}: ${r.correct} de ${r.total} acertos`}
              >
                {/* trilha fina de fundo */}
                <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-slate-100" />
                {/* barra preenchida — mais grossa, verde */}
                <div
                  className="absolute left-0 top-1/2 h-2.5 -translate-y-1/2 rounded-full bg-emerald-500"
                  style={{ width: `${acc}%` }}
                />
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ReviewCard({ q }: { q: ReviewQuestion }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy-400">
          Questão {q.position} · {q.subject}
          {q.repeated && (
            <span
              className="cursor-help rounded-full bg-gold-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gold-700"
              title="Você já respondeu esta questão recentemente — ela voltou pra fixar o conteúdo."
            >
              Reforço
            </span>
          )}
        </p>
        {q.correct ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
            Acertou
          </span>
        ) : q.given_answer === null ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-navy-500">
            Em branco
          </span>
        ) : (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Errou</span>
        )}
      </div>
      {/* Na revisão o texto entra recolhível como na prova — quem está relendo o
          erro geralmente quer o comentário, não o texto inteiro de novo. */}
      {q.reference_text && <ReferenceText reference={q.reference_text} />}
      <div className="mt-3 text-base leading-relaxed text-navy-900">
        <RichContent html={q.statement} />
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        {q.alternatives.map((alt, i) => {
          const isCorrect = i === q.correct_index
          const isWrongPick = q.given_answer === i && !isCorrect
          return (
            <div
              key={i}
              className={`flex items-start gap-2.5 rounded-lg border p-3 ${
                isCorrect
                  ? "border-emerald-300 bg-emerald-50"
                  : isWrongPick
                    ? "border-red-300 bg-red-50"
                    : "border-slate-200 bg-white"
              }`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                  isCorrect
                    ? "bg-emerald-500 text-white"
                    : isWrongPick
                      ? "bg-red-500 text-white"
                      : "bg-slate-100 text-navy-500"
                }`}
                aria-hidden="true"
              >
                {letter(i)}
              </span>
              <span className={`pt-0.5 text-sm leading-relaxed ${isCorrect || isWrongPick ? "text-navy-900" : "text-navy-600"}`}>
                <QuestionContent text={alt} />
                {isCorrect && <span className="sr-only"> (resposta correta)</span>}
                {isWrongPick && <span className="sr-only"> (sua resposta)</span>}
              </span>
            </div>
          )
        })}
      </div>
      {q.explanation && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gold-600">Gabarito comentado</p>
          <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-navy-800">
            <QuestionContent text={q.explanation} />
          </p>
          {q.legal_basis && (
            <p className="mt-2 text-xs text-navy-500">
              <span className="font-semibold text-navy-700">Fundamento:</span> {q.legal_basis}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function TopBar() {
  return (
    <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
        <BrandLogo />
        <Link to="/dashboard" className="text-sm font-medium text-navy-600 transition-colors hover:text-navy-900">
          ← Início
        </Link>
      </div>
    </div>
  )
}

// O corte da lista escolhida, montado no cliente para o veredito mudar na hora
// em que a pessoa escolhe a lista (sem recarregar o resultado).
function corteDaLista(cortes: Cortes | null, lista: string | null): Corte | null {
  if (!cortes || !lista) return null
  const l = cortes.listas.find((x) => x.key === lista)
  if (!l) return null
  return { nota: l.nota, ano: cortes.ano, lista: l.key, rotulo: l.rotulo, detalhe: `área ${cortes.area}, ${LISTAS[l.key] ?? l.rotulo}`, fonte: cortes.fonte }
}

const GOLD =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-gold-400 px-6 py-3.5 text-center font-display text-base font-bold uppercase tracking-wider text-navy-950 shadow-sm transition-colors hover:bg-gold-300 disabled:cursor-not-allowed disabled:opacity-60"

/**
 * O tratamento: os bullets, o preço e o botão do plano, iguais em toda
 * situação — muda só a manchete e a abertura que o chamador passa. Só aparece
 * para quem NÃO tem plano; quem tem já está em tratamento.
 */
function Tratamento({
  titulo,
  abertura,
  partida,
  corte,
  nota,
  aproximada,
  provaSocial,
  oferta,
  contraste = TRATAMENTO_CONTRASTE,
}: {
  titulo: string
  abertura: React.ReactNode
  partida: SubjectResult[]
  corte: Corte | null
  /** Nota pela fórmula da banca (nula sem fórmula ou em treino de uma matéria). */
  nota: number | null
  /** Nivelamento: distância arredondada a meio ponto, "cerca de". */
  aproximada: boolean
  provaSocial: ProvaSocial | undefined
  oferta: Oferta | null
  contraste?: string
}) {
  const social = provaSocialLabel(provaSocial)
  const distancia = distanciaDoCorte(nota, corte, { aproximada })
  const n = partida.length
  const itens = [
    n === 1
      ? "Fortalecimento: treinos que puxam mais questões da matéria em que você perde ponto."
      : n > 1
        ? `Fortalecimento: treinos que puxam mais questões das ${n} matérias em que você perde ponto.`
        : "Fortalecimento: treinos que puxam mais questões de onde você perde ponto.",
    "Oficial: a prova inteira, no formato e no tempo da banca, quantas vezes precisar — e a nota pela fórmula do edital em cada uma.",
    social ? `${social}, para estudar pelos seus erros.` : "Cada questão com a resposta certa e o comentário, para estudar pelos seus erros.",
    distancia
      ? `Linha de Corte: a distância até o corte da sua lista (hoje, ${distancia}) recalculada a cada simulado — pronto é passar dele em dois oficiais seguidos.`
      : "Linha de Corte: pronto é passar do corte da sua lista em dois oficiais seguidos.",
  ]

  return (
    <div>
      <h2 className="font-display text-lg font-bold uppercase tracking-wide text-navy-900">{titulo}</h2>
      <p className="mt-2 text-sm leading-relaxed text-navy-700">{abertura}</p>

      <p className="mt-4 text-[10px] font-bold uppercase tracking-widest text-gold-600">O tratamento — o que o plano faz</p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {itens.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-navy-800">
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p className="mt-3 text-sm font-semibold text-navy-900">{contraste}</p>
      {precoLinha(oferta) && <p className="mt-1 text-xs text-navy-500">{precoLinha(oferta)}</p>}

      <div className="mt-5 flex flex-col gap-2">
        <Link to="/plano?origem=resultado" className={`${GOLD} sm:self-start`}>
          {CTA_TRATAMENTO}
        </Link>
        <p className="text-xs text-navy-500">{GARANTIA} Pix à vista ou cartão em até 3x. Nada renova sozinho.</p>
      </div>
    </div>
  )
}

/**
 * O próximo passo, na lógica diagnóstico → tratamento. Quem tem plano já está
 * em tratamento e vê o treino. Quem não tem vê, nesta ordem: o diagnóstico
 * grátis da matéria mais fraca, se ainda sobrar um — e a ponte para o
 * tratamento logo abaixo dele; senão, o pitch inteiro. Acima do corte o ângulo
 * muda: diagnóstico não é prova.
 *
 * A manchete é sempre o veredito ou a distância até o corte; preço, dias e
 * garantia são detalhe embaixo, nunca a manchete.
 */
function ProximoPasso({
  partida,
  passou,
  oficial,
  criterio,
  corte,
  nota,
  aproximada,
  provaSocial,
  oferta,
  hasAccess,
  weakestFree,
  blueprintId,
  starting,
  onStartFocused,
}: {
  partida: SubjectResult[]
  passou: boolean | null
  oficial: boolean
  criterio: string
  corte: Corte | null
  nota: number | null
  aproximada: boolean
  provaSocial: ProvaSocial | undefined
  oferta: Oferta | null
  hasAccess: boolean
  weakestFree: boolean
  blueprintId: string
  starting: boolean
  onStartFocused: () => void
}) {
  const w = partida[0]?.subject
  const lacunas = nomearLacunas(partida)
  const nomeadas = listarMaterias(partida)
  const distancia = distanciaDoCorte(nota, corte, { aproximada })
  // Sem plano o passo ainda é diagnosticar; treinar é o que o plano destrava.
  const focusButton = (label: string) => (
    <button type="button" onClick={onStartFocused} disabled={starting} className={GOLD}>
      {starting && <Spinner className="h-5 w-5 text-navy-950" />}
      {label}
    </button>
  )

  const card = "rounded-2xl border border-gold-400 bg-gold-50/70 p-5 shadow-sm sm:p-6"

  // ---- Acima do corte da lista: diagnóstico não é prova ----
  if (passou === true) {
    const abertura = (
      <>
        Mas diagnóstico não é prova — a banca cobra consistência sob pressão e tempo. O tratamento: treinar no formato
        oficial até passar do corte em dois simulados seguidos, com folga.
      </>
    )
    if (!hasAccess) {
      return (
        <section aria-label="Seu próximo passo" className={card}>
          <Tratamento
            titulo="Diagnóstico: você já está acima do corte da sua lista."
            abertura={abertura}
            partida={partida}
            corte={corte}
            nota={nota}
            aproximada={aproximada}
            provaSocial={provaSocial}
            oferta={oferta}
            contraste={TRATAMENTO_CONTRASTE_ACIMA}
          />
        </section>
      )
    }
    return (
      <section aria-label="Seu próximo passo" className={card}>
        <h2 className="font-display text-lg font-bold uppercase tracking-wide text-navy-900">
          {oficial ? "Seu próximo passo: confirmar de novo no formato da banca" : "Seu próximo passo: confirmar no formato da banca"}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-navy-700">
          Um simulado não é a prova. {criterio} {PROMESSA.replace(/^C/, "Aí c")}
        </p>
        <div className="mt-5">
          <Link to={`/prova/${blueprintId}`} className={GOLD}>
            {CTA_TRATAMENTO_CONTINUAR}
          </Link>
        </div>
      </section>
    )
  }

  // ---- Com plano: já está em tratamento ----
  if (hasAccess) {
    return (
      <section aria-label="Seu próximo passo" className={card}>
        <h2 className="font-display text-lg font-bold uppercase tracking-wide text-navy-900">
          {partida.length > 0 ? `Seu próximo passo: fechar ${lacunas}` : "Seu próximo passo: treinar no formato da banca"}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-navy-700">
          Não precisa estudar tudo de novo. Estude pelo gabarito comentado dessas matérias, com a resposta certa e o
          comentário de cada questão, e treine no formato da banca até passar do corte com folga. {criterio}
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          {w ? focusButton(`Treinar ${w} agora`) : (
            <Link to={`/prova/${blueprintId}`} className={GOLD}>
              {CTA_TRATAMENTO_CONTINUAR}
            </Link>
          )}
          {w && <p className="text-xs text-navy-500">Treino curto, montado nas suas matérias mais fracas.</p>}
        </div>
      </section>
    )
  }

  // Abertura do pitch: o diagnóstico cumpriu o papel dele e agora passa a bola.
  // A tranquilização sobre errar NÃO entra aqui: o hero já a dá, logo acima, e
  // as duas condições sempre coincidem — dizer de novo três parágrafos depois
  // era o mesmo consolo com dois números diferentes.
  const abertura = (
    <>
      O diagnóstico fez o trabalho dele — mostrou exatamente onde você perde ponto
      {nomeadas ? <>: {nomeadas}</> : ""}. Mas diagnóstico não aprova ninguém: o que aprova é o tratamento — estudar só
      o que te derruba, no formato da banca, até passar do corte em dois simulados oficiais seguidos.
    </>
  )
  const tituloPitch = distancia
    ? `Diagnóstico pronto: você está a ${distancia} do corte da sua lista.`
    : partida.length > 3
      ? `Diagnóstico pronto: você perde ponto em ${partida.length} matérias.`
      : partida.length > 0
        ? `Diagnóstico pronto: você perde ponto em ${lacunas}.`
        : "Diagnóstico pronto."

  // ---- Sem plano, com o diagnóstico grátis da mais fraca sobrando ----
  // Ele vem primeiro (é o passo grátis que a pessoa pode dar agora) e a ponte
  // para o tratamento fica logo abaixo, sem competir com o botão.
  if (weakestFree && w) {
    return (
      <>
        <section aria-label="Seu próximo passo" className={card}>
          <h2 className="font-display text-lg font-bold uppercase tracking-wide text-navy-900">{tituloPitch}</h2>
          <p className="mt-2 text-sm leading-relaxed text-navy-700">{abertura}</p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            {focusButton(`Fazer o diagnóstico de ${w}`)}
            <p className="text-xs text-navy-500">Diagnóstico gratuito: 10 questões só de {w}.</p>
          </div>
        </section>
        <section aria-label="O tratamento" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <Tratamento
            titulo="Diagnóstico feito. Quer o tratamento?"
            abertura={
              <>
                O diagnóstico gratuito mostrou onde você perde ponto. O plano é o tratamento — feito exatamente para
                esse momento.
              </>
            }
            partida={partida}
            corte={corte}
            nota={nota}
            aproximada={aproximada}
            provaSocial={provaSocial}
            oferta={oferta}
          />
        </section>
      </>
    )
  }

  // ---- Sem plano e sem diagnóstico grátis sobrando: o pitch inteiro ----
  return (
    <section aria-label="Seu próximo passo" className={card}>
      <Tratamento
        titulo={tituloPitch}
        abertura={abertura}
        partida={partida}
        corte={corte}
        nota={nota}
        aproximada={aproximada}
        provaSocial={provaSocial}
        oferta={oferta}
      />
    </section>
  )
}

export default function Resultado() {
  const { id } = useParams<{ id: string }>()
  const { me } = useMe()
  const { start, startingId } = useStartSimulado()
  const [data, setData] = useState<ResultResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>("all")
  // A lista escolhida aqui mesmo (seletor no hero) recalcula o veredito na hora.
  const [listaEscolhida, setListaEscolhida] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<ResultResponse>(`/api/attempts/${id}/result`)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Erro ao carregar o resultado"))
  }, [id])

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-100 px-6 text-center">
        <p className="text-navy-700">{error}</p>
        <Link
          to="/dashboard"
          className="rounded-xl bg-navy-800 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-900"
        >
          Voltar ao início
        </Link>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <p className="animate-pulse text-sm font-medium text-navy-500">Corrigindo seu simulado...</p>
      </div>
    )
  }

  const { attempt, first_simulado, subject_results, free_trainings, review, school, cortes, nota_banca, faixa, criterio_de_pronto, prova_social } = data
  const wrong = review.filter((q) => !q.correct)
  const visible = filter === "all" ? review : wrong

  const score = attempt.score ?? 0
  const lista = listaEscolhida ?? data.lista
  const corte = corteDaLista(cortes, lista)
  // Veredito só na régua certa: nota pela fórmula da banca (prova completa)
  // contra o corte da lista. Treino de uma matéria não decide "passou".
  const notaBancaCompleta = nota_banca?.completa ? nota_banca.nota : null
  const passou = passariaDoCorte(notaBancaCompleta, corte)
  const oficial = attempt.mode === "oficial"
  const partida = materiasDePartida(subject_results)
  const fechadas = materiasFechadas(subject_results)
  const weakest = partida[0] ?? null
  const hasAccess = me?.access.active ?? false
  // Sem plano, o treino da matéria mais fraca ainda pode ser o grátis dela.
  const weakestFree = weakest != null && free_trainings[weakest.slug] === true
  const startingFocused = startingId === attempt.blueprint_id

  // Um toque: com plano, Curta de Fortalecimento (o builder mira as matérias
  // fracas); sem plano, o diagnóstico grátis da matéria mais fraca (Específico, uma
  // matéria). O hook cuida de erros (402 → /plano).
  function startFocused() {
    if (hasAccess) {
      void start({ id: attempt.blueprint_id, active_attempt_id: null }, { mode: "curta", content_mode: "fortalecimento", origem: "resultado" })
    } else if (weakest) {
      void start(
        { id: attempt.blueprint_id, active_attempt_id: null },
        { mode: "curta", content_mode: "especifico", subjects: [weakest.slug], origem: "resultado" },
      )
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 text-navy-900">
      <TopBar />

      <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-8">
        {/* Veredito no topo — nunca a nota crua primeiro */}
        <ResultHero
          school={school}
          corte={corte}
          cortes={cortes}
          lista={lista}
          onLista={setListaEscolhida}
          notaBanca={nota_banca}
          faixa={faixa}
          first={first_simulado}
          oficial={oficial}
          treino={attempt.content_mode !== "completo"}
          passou={passou}
          score={score}
          correctCount={attempt.correct_count}
          totalQuestions={attempt.total_questions}
          startedAt={attempt.started_at}
          expired={attempt.status === "expired"}
        />

        {/* Matérias de partida primeiro: é o que a pessoa veio descobrir */}
        {subject_results.length > 0 && (
          <section aria-label="Resultado por matéria" className="flex flex-col gap-3">
            <SubjectBreakdown results={partida} title="Suas matérias de partida" empty="Nenhuma. Você fechou todas." />
            <SubjectBreakdown results={fechadas} title="Onde você já está bem" empty={first_simulado ? "Nenhuma matéria fechada ainda — normal no nivelamento." : "Nenhuma matéria fechada ainda."} />
          </section>
        )}

        <ProximoPasso
          partida={partida}
          passou={passou}
          oficial={oficial}
          criterio={criterio_de_pronto}
          corte={corte}
          nota={notaBancaCompleta}
          aproximada={first_simulado || attempt.mode === "curta"}
          provaSocial={prova_social}
          oferta={me?.oferta ?? null}
          hasAccess={hasAccess}
          weakestFree={weakestFree}
          blueprintId={attempt.blueprint_id}
          starting={startingFocused}
          onStartFocused={startFocused}
        />

        {/* Revisão — o gabarito comentado é sempre liberado */}
        {review.length > 0 && (
          <section aria-label="Revisão das questões">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-navy-400">Revisão</h2>
              <div className="flex rounded-lg border border-slate-200 bg-white p-0.5" role="group" aria-label="Filtrar questões">
                <button
                  type="button"
                  onClick={() => setFilter("all")}
                  aria-pressed={filter === "all"}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    filter === "all" ? "bg-navy-800 text-white" : "text-navy-500 hover:text-navy-800"
                  }`}
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={() => setFilter("wrong")}
                  aria-pressed={filter === "wrong"}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    filter === "wrong" ? "bg-navy-800 text-white" : "text-navy-500 hover:text-navy-800"
                  }`}
                >
                  Só erradas ({wrong.length})
                </button>
              </div>
            </div>
            {visible.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white py-8 text-center shadow-sm">
                <p className="text-sm font-medium text-emerald-600">Nenhuma questão errada. Gabaritou! 🎯</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {visible.map((q) => (
                  <ReviewCard key={q.id} q={q} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  )
}
